<script setup lang="ts">
import {
  useSupabaseClient,
  useI18n,
  useDialog,
  ref,
  useAsyncData,
  defineAsyncComponent,
  useToast,
} from '#imports';
const supabase = useSupabaseClient();
const { t } = useI18n();
const dialog = useDialog();
const userId = ref<string | null>(null);
const { showError } = useToast();

const props = defineProps({
  creator: {
    type: String,
    required: true,
  },
  iconOnly: {
    type: Boolean,
    default: false,
  },
});

const { data: followData, refresh: refreshFollowData } = await useAsyncData(
  async () => {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) throw new Error(t('messages.account.notAuth'));

    userId.value = userData.user.id;

    if (props.creator.id) {
      const { data, error } = await supabase
        .from('followers')
        .select('*')
        .eq('user_id', userId.value)
        .eq('creator_id', props.creator.id);

      if (error) {
        if (error.code !== 'PGRST116')
          showError(t('messages.maps.errorLoading'));
        return null;
      }

      if (data.length === 0) return null;
      return data[0];
    }
  },
  {
    lazy: true,
  },
);

const handleFollowOperation = async (operation: 'insert' | 'delete') => {
  if (!userId.value) {
    showAuthDialog();
    return;
  }

  const payload =
    operation === 'insert'
      ? { user_id: userId.value, creator_id: props.creator }
      : undefined;

  const query = supabase.from('followers')[operation](payload);

  if (operation === 'delete') {
    query.eq('user_id', userId.value).eq('creator_id', props.creator);
  }

  const { error } = await query;

  if (error) {
    showError(
      t(
        `messages.likes.error-${operation === 'insert' ? 'adding' : 'removing'}-like`,
      ),
    );
  }

  refreshFollowData();
};

const addFollow = () => handleFollowOperation('insert');
const removeFollow = () => handleFollowOperation('delete');

const showAuthDialog = () => {
  const dynamicComponent = defineAsyncComponent(
    () => import('@/components/MPAuth/MPAuth.vue'),
  );
  dialog.open(dynamicComponent, {
    /*
    onCancel: (e) => {
      // console.log(e)
    },
    onAuthSuccess: (e) => {
      // console.log(e)
    },
     */
    props: {
      style: {
        width: '20rem',
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
      modal: true,
      dismissableMask: true,
      blockScroll: true,
    },
  });
};
</script>

<template>
  <div v-if="iconOnly">
    <MPButton
      rounded
      outline
      label="Follow"
      hide-label
      icon="ri-user-add-line"
      @click="addFollow"
    />
  </div>
  <div v-else>
    <MPButton
      v-if="!followData"
      label="Follow"
      class="follow-button"
      version="primary"
      size="small"
      rounded
      outline
      aria-label="Follow"
      @click="addFollow"
    />

    <MPButton
      v-else
      label="Unfollow"
      class="follow-button"
      version="secondary"
      size="small"
      rounded
      outline
      aria-label="Follow"
      @click="removeFollow"
    />
  </div>
</template>

<style scoped>
.follow-button {
  background-color: var(--color-rose-400);
}
</style>
